import React, { RefObject } from 'react'

import { SelectItemsChangeOptions } from '../SelectBoxItem'

interface AriaControllerProps<TOption> {
  dropdownRef: React.RefObject<HTMLDivElement>
  isOpen: boolean
  renderedItems: TOption[]
  handleSelectItem: (option: TOption, options?: SelectItemsChangeOptions) => void
  clearSelectBox: () => void
  switchOpen: () => void
  showSearchInput?: boolean
}

const focusElement = (ref: React.RefObject<HTMLElement>, index: number) => {
  const children = getListChildren(ref)
  if (children && children[index]) {
    children[index]?.focus()
    ref.current?.setAttribute('data-focus-index', `${index}`)
  }
}

const getCurrentFocusIndex = (ref: React.RefObject<HTMLElement>) =>
  parseInt(ref.current?.getAttribute('data-focus-index') || '0')

const getListChildren = (ref: React.RefObject<HTMLElement>) => {
  const ul = ref.current?.querySelector('ul[role="listbox"]')
  return ul ? (Array.from(ul.children) as HTMLElement[]) : []
}

const keyPress = <TOption, T extends HTMLDivElement>(
  isOpen: boolean,
  renderedItems: TOption[],
  handleSelectItem: (option: TOption, options?: SelectItemsChangeOptions) => void,
  clearSelectBox: () => void,
  switchOpen: () => void,
  elementRef: RefObject<T>
): Record<string, (preventDefault: () => void) => void> | undefined => {
  const skipCount = 10
  return {
    ArrowDown: (preventDefault) => {
      preventDefault()
      if (isOpen) {
        const children = getListChildren(elementRef)
        const index = getCurrentFocusIndex(elementRef)
        focusElement(elementRef, (index + 1) % children.length)
      } else {
        switchOpen()
        focusElement(elementRef, 0)
      }
    },
    ArrowRight: (preventDefault) => {
      preventDefault()
      if (isOpen) {
        const children = getListChildren(elementRef)
        const index = getCurrentFocusIndex(elementRef)
        focusElement(elementRef, (index + 1) % children.length)
      } else {
        switchOpen()
        focusElement(elementRef, 0)
      }
    },
    ArrowUp: (preventDefault) => {
      preventDefault()
      if (isOpen) {
        const children = getListChildren(elementRef)
        const index = getCurrentFocusIndex(elementRef)
        focusElement(elementRef, (index - 1 + children.length) % children.length)
      } else {
        switchOpen()
        const children = getListChildren(elementRef)
        focusElement(elementRef, children.length - 1)
      }
    },
    ArrowLeft: (preventDefault) => {
      preventDefault()
      if (isOpen) {
        const children = getListChildren(elementRef)
        const index = getCurrentFocusIndex(elementRef)
        focusElement(elementRef, (index - 1 + children.length) % children.length)
      } else {
        switchOpen()
        focusElement(elementRef, 0)
      }
    },
    Enter: (preventDefault) => {
      preventDefault()
      if (isOpen && renderedItems.length > 0 && elementRef && !elementRef.current?.querySelector('input:focus')) {
        handleSelectItem(renderedItems[getCurrentFocusIndex(elementRef)] as TOption, { forceClose: true })
        clearSelectBox()
      } else {
        switchOpen()
      }
    },
    Space: (preventDefault) => {
      const activeElement = document.activeElement?.tagName.toLowerCase() === 'input'
      if (isOpen && renderedItems.length > 0 && !activeElement) {
        preventDefault()
        handleSelectItem(renderedItems[getCurrentFocusIndex(elementRef)] as TOption, { forceClose: true })
        clearSelectBox()
      } else if (!isOpen) {
        switchOpen()
      }
    },
    Home: () => {
      switchOpen()
      focusElement(elementRef, 0)
    },
    End: (preventDefault) => {
      preventDefault()
      switchOpen()
      const children = getListChildren(elementRef)
      focusElement(elementRef, children.length - 1)
    },
    PageUp: (preventDefault) => {
      if (isOpen) {
        preventDefault()
        const children = getListChildren(elementRef)
        const index = getCurrentFocusIndex(elementRef)
        focusElement(elementRef, children.length > skipCount ? Math.max(0, index - skipCount) : 0)
      }
    },
    PageDown: (preventDefault) => {
      if (isOpen) {
        preventDefault()
        const children = getListChildren(elementRef)
        const index = getCurrentFocusIndex(elementRef)
        focusElement(
          elementRef,
          children.length > skipCount ? Math.min(children.length - 1, index + skipCount) : children.length - 1
        )
      }
    },
    Tab: () => clearSelectBox(),
    Escape: () => clearSelectBox()
  }
}

export const useAriaController = <TOption>({
  dropdownRef,
  isOpen,
  renderedItems,
  handleSelectItem,
  clearSelectBox,
  switchOpen,
  showSearchInput
}: AriaControllerProps<TOption>) => {
  const selectBoxRef = dropdownRef as RefObject<HTMLDivElement>

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const keyActions = keyPress<TOption, HTMLDivElement>(
      isOpen,
      renderedItems,
      handleSelectItem,
      clearSelectBox,
      switchOpen,
      dropdownRef
    )

    if (keyActions && keyActions[e.code]) {
      keyActions[e.code]?.(() => e.preventDefault())
    } else {
      selectBoxRef.current?.querySelector(showSearchInput ? 'div' : 'input')?.focus()
    }
  }

  return { selectBoxRef, handleDropDownKeyAction: handleKeyDown, focusElement, getListChildren }
}
